import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BookPlus, Target, Check, X, Trash2, MessageSquare, Filter, Users } from 'lucide-react';
import { suggestionsService } from '../../services/suggestionsService';
import { authService } from '../../services/authService';
import { useToast } from '../../hooks/use-toast';

const AdminSuggestions = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bookSuggestions, setBookSuggestions] = useState([]);
  const [quizRequests, setQuizRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('books');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingItem, setEditingItem] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    // Check authentication
    if (!authService.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    
    fetchData();
  }, [navigate, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const filter = statusFilter === 'all' ? null : statusFilter;
      
      const [suggestions, requests] = await Promise.all([
        suggestionsService.getBookSuggestions(filter),
        suggestionsService.getQuizRequests(filter)
      ]);
      
      setBookSuggestions(suggestions);
      setQuizRequests(requests);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load suggestions and requests.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBookSuggestionAction = async (suggestion, action, notes = '') => {
    try {
      await suggestionsService.updateBookSuggestion(suggestion.id, {
        status: action,
        admin_notes: notes
      });
      
      toast({
        title: "Success",
        description: `Book suggestion ${action === 'approved' ? 'approved' : 'rejected'} successfully!`,
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating suggestion:', error);
      toast({
        title: "Error",
        description: "Failed to update book suggestion.",
        variant: "destructive",
      });
    }
  };

  const handleQuizRequestAction = async (request, action, notes = '') => {
    try {
      await suggestionsService.updateQuizRequest(request.id, {
        status: action,
        admin_notes: notes
      });
      
      toast({
        title: "Success",
        description: `Quiz request ${action === 'approved' ? 'approved' : 'rejected'} successfully!`,
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating request:', error);
      toast({
        title: "Error",
        description: "Failed to update quiz request.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
      return;
    }

    try {
      if (type === 'suggestion') {
        await suggestionsService.deleteBookSuggestion(id);
      } else {
        await suggestionsService.deleteQuizRequest(id);
      }
      
      toast({
        title: "Success",
        description: `${type === 'suggestion' ? 'Book suggestion' : 'Quiz request'} deleted successfully!`,
      });
      
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      toast({
        title: "Error",
        description: `Failed to delete ${type}.`,
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Layout isAdmin={true}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  const currentItems = activeTab === 'books' ? bookSuggestions : quizRequests;

  return (
    <Layout isAdmin={true}>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">User Suggestions & Requests</h1>
                <p className="text-gray-600">Manage book suggestions and quiz requests from users</p>
              </div>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
            <button
              onClick={() => setActiveTab('books')}
              className={`flex items-center space-x-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'books'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <BookPlus className="h-4 w-4" />
              <span>Book Suggestions ({bookSuggestions.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('quizzes')}
              className={`flex items-center space-x-2 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'quizzes'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Target className="h-4 w-4" />
              <span>Quiz Requests ({quizRequests.length})</span>
            </button>
          </div>

          {/* Admin Notes Modal */}
          {editingItem && (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0 mb-8">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageSquare className="h-5 w-5 mr-2" />
                  Add Admin Notes
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="adminNotes">Admin Notes</Label>
                  <Textarea
                    id="adminNotes"
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={3}
                    placeholder="Add notes about your decision..."
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      if (activeTab === 'books') {
                        handleBookSuggestionAction(editingItem, 'approved', adminNotes);
                      } else {
                        handleQuizRequestAction(editingItem, 'approved', adminNotes);
                      }
                      setEditingItem(null);
                      setAdminNotes('');
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Approve with Notes
                  </Button>
                  <Button
                    onClick={() => {
                      if (activeTab === 'books') {
                        handleBookSuggestionAction(editingItem, 'rejected', adminNotes);
                      } else {
                        handleQuizRequestAction(editingItem, 'rejected', adminNotes);
                      }
                      setEditingItem(null);
                      setAdminNotes('');
                    }}
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Reject with Notes
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingItem(null);
                      setAdminNotes('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Items List */}
          <div className="space-y-4">
            {currentItems.map((item) => (
              <Card key={item.id} className="bg-white/80 backdrop-blur-sm shadow-lg border-0 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getStatusColor(item.status)}>
                          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                      
                      {activeTab === 'books' ? (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            "{item.title}" by {item.author}
                          </h3>
                          {item.isbn && (
                            <p className="text-sm text-gray-600 mb-2">ISBN: {item.isbn}</p>
                          )}
                        </div>
                      ) : (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            Quiz Request
                          </h3>
                          {item.difficulty_requested && (
                            <Badge variant="outline" className="mb-2">
                              {item.difficulty_requested} Difficulty
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      <p className="text-gray-700 mb-3">{item.reason}</p>
                      
                      {(item.user_name || item.user_email) && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <Users className="h-4 w-4 mr-1" />
                          {item.user_name && <span>{item.user_name}</span>}
                          {item.user_name && item.user_email && <span className="mx-1">•</span>}
                          {item.user_email && <span>{item.user_email}</span>}
                        </div>
                      )}
                      
                      {item.admin_notes && (
                        <div className="bg-gray-50 border border-gray-200 rounded p-3 mt-3">
                          <span className="text-sm font-medium text-gray-900">Admin Notes: </span>
                          <span className="text-sm text-gray-700">{item.admin_notes}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      {item.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => {
                              if (activeTab === 'books') {
                                handleBookSuggestionAction(item, 'approved');
                              } else {
                                handleQuizRequestAction(item, 'approved');
                              }
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (activeTab === 'books') {
                                handleBookSuggestionAction(item, 'rejected');
                              } else {
                                handleQuizRequestAction(item, 'rejected');
                              }
                            }}
                            className="border-red-200 text-red-700 hover:bg-red-50"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingItem(item);
                              setAdminNotes(item.admin_notes || '');
                            }}
                          >
                            <MessageSquare className="h-3 w-3 mr-1" />
                            Notes
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(item.id, activeTab === 'books' ? 'suggestion' : 'request')}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {currentItems.length === 0 && (
            <div className="text-center py-12">
              {activeTab === 'books' ? (
                <BookPlus className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              ) : (
                <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              )}
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No {activeTab === 'books' ? 'book suggestions' : 'quiz requests'} found
              </h3>
              <p className="text-gray-600">
                {statusFilter === 'all' 
                  ? `Users haven't submitted any ${activeTab === 'books' ? 'book suggestions' : 'quiz requests'} yet.`
                  : `No ${activeTab === 'books' ? 'book suggestions' : 'quiz requests'} with status "${statusFilter}".`
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminSuggestions;